import { useState } from "react";
import { Copy, Send } from "lucide-react";

import { api, errMsg } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { cn } from "../../../lib/utils.js";
import { AsyncButton } from "../../ui/async-button.js";
import { Badge } from "../../ui/badge.js";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";
import { Tooltip } from "../../ui/tooltip.js";
import type { OnboardingRecord } from "./OnboardingSection.js";

interface SendResult { sentTo: string; link: string; expiresAt: string; emailed: boolean; emailError: string | null }

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// HR sends the new hire a secure, no-login link to their onboarding wizard at
// their personal email. The link is also shown so HR can share it directly
// when email is off on this environment or the send failed.
export function OnboardingLinkPanel({ employeeId, personalEmail, record, onSent }: {
  employeeId: string;
  personalEmail: string;
  record: Pick<OnboardingRecord, "status" | "activatedAt" | "accessLinkSentAt" | "accessLinkSentTo" | "accessTokenExpiresAt">;
  onSent: () => Promise<unknown>;
}) {
  const [result, setResult] = useState<SendResult | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const completed = !!record.activatedAt;
  const hasPersonalEmail = personalEmail.trim() !== "";
  const disabledReason = completed
    ? "Onboarding is already complete."
    : !hasPersonalEmail
      ? "Add and save the employee's personal email first."
      : null;
  const linkExpired = !!record.accessTokenExpiresAt && new Date(record.accessTokenExpiresAt) < new Date();

  async function send() {
    setError(""); setCopied(false);
    try {
      // HRMS-0638: once the hire has submitted for HR review, a plain resend
      // leaves them stuck on the read-only SubmittedView. Switch to the
      // reopen-and-resend endpoint, which flips status back to IN_PROGRESS
      // before minting the new link, so the fresh link lands on the editable
      // wizard.
      const path = record.status === "PENDING_HR_REVIEW"
        ? `/employees/${employeeId}/onboarding/reopen-and-resend`
        : `/employees/${employeeId}/onboarding/send-link`;
      const { data } = await api.post<SendResult>(path, {});
      setResult(data);
      await onSent();
    } catch (e) {
      logError("OnboardingLinkPanel.send", e, { employeeId });
      setError(errMsg(e) ?? "Could not send the onboarding link.");
      throw e;
    }
  }

  async function copy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
    } catch (e) {
      logError("OnboardingLinkPanel.copy", e);
    }
  }

  return (
    <div className="border-b border-surface-100 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-[1_1_180px]">
          <div className="text-[13px] font-medium">Onboarding link</div>
          <div className="text-xs text-surface-500">
            {record.accessLinkSentAt
              ? <>Sent to {record.accessLinkSentTo} on {fmtDate(record.accessLinkSentAt)}{record.accessTokenExpiresAt && <> · {linkExpired ? "expired" : "expires"} {fmtDate(record.accessTokenExpiresAt)}</>}</>
              : "Not sent yet. The employee completes onboarding from this link, no login needed."}
          </div>
        </div>
        <Tooltip content={disabledReason}>
          {/* Wrapper keeps the tooltip working while the button is disabled. */}
          <span className="inline-flex">
            <AsyncButton onClick={() => send()} disabled={!!disabledReason} size="sm" variant={record.accessLinkSentAt ? "outline" : "default"} animated={!record.accessLinkSentAt}>
              <Send size={12} /> {record.accessLinkSentAt ? "Resend link" : "Send onboarding link"}
            </AsyncButton>
          </span>
        </Tooltip>
      </div>

      {error && <p className="mt-2 text-xs text-[var(--color-error-fg)]">{error}</p>}

      {result && (
        <div className={cn(
          "mt-2 rounded-md border p-3",
          result.emailed
            ? "border-[var(--color-success-border)] bg-[var(--color-success-bg-subtle)]"
            : "border-[var(--color-warning-border)] bg-[var(--color-warning-bg-subtle)]",
        )}>
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
            <Badge tone={result.emailed ? "success" : "warning"}>{result.emailed ? "Emailed" : "Not emailed"}</Badge>
            <span className={result.emailed ? "text-[var(--color-success-fg)]" : "text-[var(--color-warning-fg)]"}>
              {result.emailed
                ? `Sent to ${result.sentTo}. A new link replaces any earlier one.`
                : `Email not sent — ${result.emailError ?? "unknown reason"}. Share this link with the employee yourself:`}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input readOnly value={result.link} className="h-8 min-w-0 flex-1 text-xs" onFocus={(e) => e.currentTarget.select()} />
            <Button size="sm" variant="secondary" onClick={() => void copy()}>
              <Copy size={12} /> {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-surface-500">Expires {fmtDate(result.expiresAt)} · personal to this employee · opens only their onboarding form</p>
        </div>
      )}
    </div>
  );
}
